import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import PricingDatePicker from '../components/PricingDatePicker';
import RijekaButton from '../components/RijekaButton';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ShieldCheck, CalendarCheck, Headphones, Star, Clock, MapPin, CreditCard, Users, Heart, Award, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping for info cards
const ICON_MAP = {
  'shield-check': ShieldCheck,
  'calendar-check': CalendarCheck,
  'headphones': Headphones,
  'star': Star,
  'clock': Clock,
  'map-pin': MapPin,
  'credit-card': CreditCard,
  'users': Users,
  'heart': Heart,
  'award': Award,
  'zap': Zap,
};

const HomePage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { theme, getHeroTitle, getHeroSubtitle, getSectionTitle, getSectionSubtitle } = useTheme();
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [showGuestSelector, setShowGuestSelector] = useState(false);
  const [showPropertySelector, setShowPropertySelector] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [infoCards, setInfoCards] = useState([]);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties/list`);
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  const fetchInfoCards = async () => {
    try {
      const response = await axios.get(`${API}/info-cards`);
      setInfoCards(response.data);
    } catch (error) {
      console.error('Error fetching info cards:', error);
    }
  };

  useEffect(() => {
    fetchProperties();
    fetchInfoCards();
  }, []);

  const handleDateSelect = (checkInDate, checkOutDate) => {
    setCheckIn(checkInDate);
    setCheckOut(checkOutDate);
  };

  // Close other selectors when calendar opens
  const handleCalendarOpenChange = (isOpen) => {
    setCalendarOpen(isOpen);
    if (isOpen) {
      setShowGuestSelector(false);
      setShowPropertySelector(false);
    }
  };

  // Close calendar and property selector when guest selector opens
  const handleGuestSelectorClick = () => {
    if (!showGuestSelector) {
      // Opening guest selector - close others
      setShowPropertySelector(false);
      // Note: calendar will close itself via click outside handler
    }
    setShowGuestSelector(!showGuestSelector);
  };

  // Close calendar and guest selector when property selector opens
  const handlePropertySelectorClick = () => {
    if (!showPropertySelector) {
      // Opening property selector - close others
      setShowGuestSelector(false);
    }
    setShowPropertySelector(!showPropertySelector);
  };

  const handleSearch = () => {
    if (!checkIn || !checkOut) {
      toast.error(t('home.pleaseSelectDates'));
      return;
    }
    
    if (adults < 1) {
      toast.error(t('home.pleaseSelectAdult'));
      return;
    }

    const formatDateForAPI = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const params = new URLSearchParams({
      propertyId: selectedProperty || '',
      adults: adults.toString(),
      children: children.toString(),
      checkIn: formatDateForAPI(checkIn),
      checkOut: formatDateForAPI(checkOut)
    });

    navigate(`/properties?${params.toString()}`);
  };

  const totalGuests = adults + children;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)', fontFamily: 'var(--font-body)' }}>
      {/* Hero Section with Background Image */}
      <div 
        className="relative min-h-screen"
        style={{
          backgroundImage: theme.hero_background_url 
            ? `url(${theme.hero_background_url})` 
            : 'linear-gradient(135deg, var(--color-dark) 0%, var(--color-dark-hover) 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          paddingBottom: '30px'
        }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/40"></div>
        
        {/* Header inside hero for transparent effect */}
        <div className="relative z-20">
          <Header transparent={true} />
        </div>
        
        {/* Content - positioned below header */}
        <div className="relative z-10 container mx-auto px-4 pt-4 pb-8 sm:pt-8 sm:pb-16">
          <div className="max-w-4xl mx-auto text-center mb-6 sm:mb-12">
            {/* Hero Title - Uses language-specific setting from theme */}
            <h1 
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3 sm:mb-6 leading-tight px-2"
              style={{ fontFamily: 'var(--font-heading)', fontWeight: 'var(--font-heading-weight, 600)', marginTop: '80px' }}
            >
              {getHeroTitle(i18n.language)}
            </h1>
            {/* Hero Subtitle - Uses language-specific setting from theme */}
            <p 
              className="text-sm sm:text-lg md:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed px-4"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              {getHeroSubtitle(i18n.language)}
            </p>
          </div>

          {/* Search Form Card */}
          <div 
            className="max-w-4xl mx-auto bg-white rounded-xl sm:rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 mx-2 sm:mx-auto"
            style={{ color: 'var(--color-text-primary)' }}
            data-testid="search-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Property Location */}
              <div className="relative">
                <Label className="text-sm font-semibold mb-3 block uppercase tracking-wide" style={{ color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
                  {t('home.propertyLocation')}
                </Label>
                <div
                  className="w-full h-14 px-4 flex items-center justify-between border-2 rounded-lg cursor-pointer bg-white hover:border-[var(--color-primary)] transition-all"
                  style={{ borderColor: 'var(--color-border)' }}
                  onClick={handlePropertySelectorClick}
                  data-testid="location-selector"
                >
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5" style={{ color: 'var(--color-primary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-sm font-medium" style={{ fontFamily: 'var(--font-body)' }}>
                      {selectedProperty ? properties.find(p => p.property_id === selectedProperty)?.property_name || t('home.allProperties') : t('home.allProperties')}
                    </span>
                  </div>
                  <svg className="w-4 h-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                
                {showPropertySelector && (
                  <div className="absolute z-50 mt-2 w-full bg-white border-2 rounded-lg shadow-2xl max-h-[300px] overflow-y-auto" style={{ borderColor: 'var(--color-primary)' }} data-testid="property-dropdown">
                    <div 
                      className="px-4 py-3 hover:bg-[var(--color-background)] cursor-pointer transition-colors"
                      onClick={() => {
                        setSelectedProperty('');
                        setShowPropertySelector(false);
                      }}
                    >
                      <span className="text-sm font-medium">{t('home.allProperties')}</span>
                    </div>
                    {properties.map((prop) => (
                      <div
                        key={prop.property_id}
                        className="px-4 py-3 hover:bg-[var(--color-background)] cursor-pointer transition-colors"
                        onClick={() => {
                          setSelectedProperty(prop.property_id);
                          setShowPropertySelector(false);
                        }}
                      >
                        <span className="text-sm font-medium">{prop.property_name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Guests Selector */}
              <div className="relative">
                <Label className="text-sm font-semibold mb-3 block uppercase tracking-wide" style={{ color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
                  {t('home.guests')}
                </Label>
                <div
                  className="w-full h-14 px-4 flex items-center justify-between border-2 rounded-lg cursor-pointer bg-white hover:border-[var(--color-primary)] transition-all"
                  style={{ borderColor: 'var(--color-border)' }}
                  onClick={handleGuestSelectorClick}
                  data-testid="guests-selector"
                >
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5" style={{ color: 'var(--color-primary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span className="text-sm font-medium" style={{ fontFamily: 'var(--font-body)' }}>
                      {totalGuests} {totalGuests !== 1 ? t('home.guests') : t('home.guest')}
                    </span>
                  </div>
                  <svg className="w-4 h-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                
                {showGuestSelector && (
                  <div className="absolute z-50 mt-2 w-full bg-white border-2 rounded-lg shadow-2xl p-4 sm:p-6" style={{ borderColor: 'var(--color-primary)' }} data-testid="guests-dropdown">
                    <div className="space-y-4 sm:space-y-6">
                      <div className="flex justify-between items-center gap-2">
                        <div className="flex-shrink-0">
                          <p className="font-semibold text-sm sm:text-base" style={{ color: 'var(--color-text-primary)' }}>{t('home.adults')}</p>
                          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{t('home.adultAge')}</p>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-4">
                          <button
                            onClick={() => setAdults(Math.max(1, adults - 1))}
                            disabled={adults <= 1}
                            className="w-10 h-10 flex items-center justify-center rounded-full transition-all text-lg font-bold flex-shrink-0 disabled:opacity-30 disabled:cursor-not-allowed"
                            style={{ backgroundColor: 'var(--color-background)', color: 'var(--color-text-primary)' }}
                            data-testid="adults-decrease"
                          >
                            −
                          </button>
                          <span className="w-10 text-center font-bold text-lg" data-testid="adults-count">{adults}</span>
                          <button
                            onClick={() => setAdults(adults + 1)}
                            className="w-10 h-10 flex items-center justify-center rounded-full transition-all text-lg font-bold flex-shrink-0"
                            style={{ backgroundColor: 'var(--color-primary)', color: 'white' }}
                            data-testid="adults-increase"
                          >
                            +
                          </button>
                        </div>
                      </div>

                      <div className="flex justify-between items-center gap-2">
                        <div className="flex-shrink-0">
                          <p className="font-semibold text-sm sm:text-base" style={{ color: 'var(--color-text-primary)' }}>{t('home.children')}</p>
                          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{t('home.childAge')}</p>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-4">
                          <button
                            onClick={() => setChildren(Math.max(0, children - 1))}
                            disabled={children <= 0}
                            className="w-10 h-10 flex items-center justify-center rounded-full transition-all text-lg font-bold flex-shrink-0 disabled:opacity-30 disabled:cursor-not-allowed"
                            style={{ backgroundColor: 'var(--color-background)', color: 'var(--color-text-primary)' }}
                            data-testid="children-decrease"
                          >
                            −
                          </button>
                          <span className="w-10 text-center font-bold text-lg" data-testid="children-count">{children}</span>
                          <button
                            onClick={() => setChildren(children + 1)}
                            className="w-10 h-10 flex items-center justify-center rounded-full transition-all text-lg font-bold flex-shrink-0"
                            style={{ backgroundColor: 'var(--color-primary)', color: 'white' }}
                            data-testid="children-increase"
                          >
                            +
                          </button>
                        </div>
                      </div>

                      <RijekaButton 
                        onClick={() => setShowGuestSelector(false)}
                        className="w-full"
                        data-testid="guests-done"
                      >
                        {t('common.confirm')}
                      </RijekaButton>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Date Picker */}
            <div className="mb-8">
              <Label className="text-sm font-semibold mb-3 block uppercase tracking-wide" style={{ color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
                {t('home.checkInCheckOut')}
              </Label>
              <PricingDatePicker 
                onDateSelect={handleDateSelect}
                selectedLocation={selectedProperty || 'all'}
                onOpenChange={handleCalendarOpenChange}
              />
            </div>

            {/* Search Button */}
            <RijekaButton 
              onClick={handleSearch} 
              className="w-full py-5 font-semibold rounded-lg"
              data-testid="search-button"
            >
              <svg className="w-5 h-5 mr-2 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {t('home.searchAvailable')}
            </RijekaButton>
          </div>
        </div>

      </div>

      {/* Features Section - "Why Book With Us" */}
      <div className="py-16 sm:py-20 pb-24 sm:pb-32" style={{ backgroundColor: 'var(--color-background-white)', marginBottom: '-30px' }}>
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl sm:text-4xl font-bold mb-4"
              style={{ color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)', fontWeight: 'var(--font-heading-weight, 600)' }}
            >
              {getSectionTitle(i18n.language)}
            </h2>
            <p 
              className="text-lg max-w-2xl mx-auto"
              style={{ color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}
            >
              {getSectionSubtitle(i18n.language)}
            </p>
          </div>

          {/* Dynamic Info Cards from Admin */}
          <div className={`grid grid-cols-1 gap-8 ${
            infoCards.length === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 
            infoCards.length === 2 ? 'md:grid-cols-2' : 
            'md:grid-cols-3'
          }`}>
            {infoCards.map((card) => {
              const IconComponent = ICON_MAP[card.icon] || Star;
              const title = card[`title_${i18n.language}`] || card.title_en || card.title_hr;
              const description = card[`description_${i18n.language}`] || card.description_en || card.description_hr;
              
              return (
                <div 
                  key={card.id} 
                  className="text-center p-8 rounded-2xl transition-all hover:shadow-lg" 
                  style={{ backgroundColor: 'var(--color-background-light)' }}
                  data-testid={`info-card-${card.id}`}
                >
                  <div 
                    className="w-16 h-16 mx-auto mb-6 flex items-center justify-center rounded-2xl"
                    style={{ backgroundColor: 'var(--color-primary)', color: 'white' }}
                  >
                    {card.custom_icon_url ? (
                      <img 
                        src={card.custom_icon_url} 
                        alt={title} 
                        className="w-10 h-10 object-contain"
                        style={{ filter: 'brightness(0) invert(1)' }}
                      />
                    ) : (
                      <IconComponent className="w-8 h-8" />
                    )}
                  </div>
                  <h3 
                    className="text-xl font-bold mb-3"
                    style={{ color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)', fontWeight: 'var(--font-heading-weight, 600)' }}
                  >
                    {title}
                  </h3>
                  <p style={{ color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)', fontSize: '14px'}}>
                    {description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default HomePage;
